import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

// ── Types ────────────────────────────────────────────────────────────

export interface CommsTimelineEvent {
  id: string;
  contact_id: string;
  campaign_id: string | null;
  campaign_name: string | null;
  message_type: string;
  messaging_variant: string | null;
  delivery_channel: string | null;
  sender_name: string;
  status: string;
  // Outbound
  sent_at: string | null;
  outbound_text: string | null;
  // Send rollup (from outreach_message_sends). `first_sent_at` is authoritative
  // for "when did this go out" — `sent_at` was historically overwritten by each
  // later Bison sequence step, so it can hold a follow-up's date.
  first_sent_at: string | null;
  last_sent_at: string | null;
  send_count: number | null;
  // Reply
  reply_received_at: string | null;
  reply_text: string | null;
  reply_category: string | null;
  reply_sentiment: string | null;
  // Handoff
  handoff_to_inbox_at: string | null;
  handoff_method: string | null;
  // Connection
  connection_accepted_at: string | null;
  // Skip
  skipped_at: string | null;
  skip_reason_category: string | null;
  skip_reason: string | null;
  // LinkedIn reply
  linkedin_reply_acknowledged_at: string | null;
  linkedin_reply_outcome: string | null;
  linkedin_reply_notes: string | null;
  // Email reply
  email_reply_outcome: string | null;
  email_reply_notes: string | null;
  // Trace IDs
  bison_lead_id: string | null;
  bison_reply_id: string | null;
  heyreach_conversation_id: string | null;
  // Timestamps
  created_at: string;
  updated_at: string;
}

export interface CampaignGroup {
  campaign_id: string | null;
  campaign_name: string | null;
  events: CommsTimelineEvent[];
}

// ── Hook ─────────────────────────────────────────────────────────────

export function useContactCommsTimeline(contactId: string | null) {
  return useQuery({
    queryKey: ["contact-comms-timeline", contactId],
    enabled: !!contactId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("v_contact_comms_timeline")
        .select("*")
        .eq("contact_id", contactId)
        .order("sent_at", { ascending: true, nullsFirst: false });
      if (error) throw error;

      const events = (data || []) as CommsTimelineEvent[];

      // Group by campaign
      const groupMap = new Map<string, CampaignGroup>();
      for (const ev of events) {
        const key = ev.campaign_id ?? "__uncategorized__";
        if (!groupMap.has(key)) {
          groupMap.set(key, {
            campaign_id: ev.campaign_id,
            campaign_name: ev.campaign_name,
            events: [],
          });
        }
        groupMap.get(key)!.events.push(ev);
      }

      return Array.from(groupMap.values());
    },
  });
}
